import React, { useEffect, useState } from "react";
import { ArrowRight, Compass, Sparkles, Quote, BookOpen } from "lucide-react";
import Navigation from "../components/site/Navigation";
import Footer from "../components/site/Footer";
import Reveal from "../components/site/Reveal";
import OrientationResetCheckoutHost from "../components/site/OrientationResetCheckoutHost";
import LeadCaptureForm from "../components/site/LeadCaptureForm";
import { api } from "../lib/api";
import { trackEvent } from "../lib/track";
import { captureUtm, getUtm } from "../lib/utm";

const SEO = {
  title: "A Quiet Doorway In · Authentic Momentum",
  description:
    "If you found Cyrus through LinkedIn or a direct introduction, this is the clearest way into Authentic Momentum. Three paths, one founder, no hype.",
  ogImage: "https://customer-assets.emergentagent.com/job_momentum-flow-12/og/linkedin-card.jpg",
};

const PATHS = [
  {
    id: "snapshot",
    eyebrow: "Path 1 · Clarity Snapshot",
    title: "Take the Clarity Snapshot",
    subtitle: "Free · 90 seconds · No login.",
    body:
      "Ten honest questions surface which of four signals your current season is pulling against. You receive a written reflection and one clear next move. No pressure, no upsell layered on top.",
    href: "/frequency-snapshot",
    cta: "Begin the Snapshot",
    icon: Compass,
    testid: "linkedin-path-snapshot",
  },
  {
    id: "orientation_reset",
    eyebrow: "Path 2 · Clarity Reset Session · $149",
    title: "Book the Clarity Reset Session",
    subtitle: "One 30-minute call with Cyrus.",
    body:
      "If you already know the friction and want a real conversation about the right next move, the Reset is the most direct route. Plain language, structured questions, one focused outcome by the end of the call.",
    href: "#book-orientation-reset",
    cta: "Book the Clarity Reset Session",
    icon: Sparkles,
    testid: "linkedin-path-orientation-reset",
    isOrientationReset: true,
  },
  {
    id: "book",
    eyebrow: "Path 3 · The Book",
    title: "Read Authentic Momentum",
    subtitle: "$19.95 · On Amazon Kindle.",
    body:
      "The same framework, written long-form. If you want to sit with the ideas before talking to anyone, the book is the foundation everything else builds on. Read it slowly. The chapters land differently when you let them.",
    href: "/book",
    cta: "Buy Authentic Momentum",
    icon: BookOpen,
    testid: "linkedin-path-book",
  },
];

const LinkedInLandingPage = () => {
  const [voices, setVoices] = useState([]);

  // Capture UTM on mount + log a dedicated landing event so we can see
  // exactly how outreach traffic engages with each path.
  useEffect(() => {
    document.title = SEO.title;
    captureUtm();
    const attribution = getUtm();
    trackEvent("linkedin_landing_viewed", { attribution });

    let cancelled = false;
    api
      .get("/testimonials")
      .then((res) => {
        if (cancelled) return;
        setVoices((res.data || []).slice(0, 3));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const handlePathClick = (path) => () => {
    trackEvent("linkedin_path_clicked", { path: path.id });
    if (path.isOrientationReset) {
      window.dispatchEvent(new CustomEvent("am:open-orientation-reset-checkout"));
    }
  };

  return (
    <>
      {/* Page metadata — set inline so search engines + LinkedIn unfurl
          correctly without a build-time HTML template rewrite. */}
      <meta name="description" content={SEO.description} />
      <Navigation />
      <main className="relative pt-32 pb-32 min-h-screen" data-testid="linkedin-landing-page">

        {/* ── Hero ───────────────────────────────────────────────── */}
        <section className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6">
              A Quiet Doorway In
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl text-themed leading-tight tracking-tight font-light">
              You came from a{" "}
              <span className="italic gold-gradient-text">conversation.</span>
            </h1>
            <p className="mt-8 text-themed-muted max-w-2xl mx-auto leading-relaxed text-base md:text-lg">
              If you found Cyrus through LinkedIn, a direct message, or a
              personal introduction, this page is the cleanest way in. No
              hype layer. No sales funnel theatre. Three honest paths and
              one founder who reads every reply.
            </p>
          </Reveal>
        </section>

        {/* ── Three Paths ───────────────────────────────────────── */}
        <section className="relative max-w-6xl mx-auto px-6 md:px-10 mt-24">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl text-themed font-light text-center mb-3">
              Pick the path that meets you{" "}
              <span className="italic gold-gradient-text">where you are.</span>
            </h2>
            <p className="text-themed-muted text-center max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
              All three eventually point to the same recalibration. Start
              with the one that feels lightest right now.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {PATHS.map((p, i) => {
              const Icon = p.icon;
              const isAnchor = p.href.startsWith("/") || p.href.startsWith("/?");
              const commonProps = {
                "data-testid": p.testid,
                onClick: handlePathClick(p),
                className: "glass-card p-7 md:p-8 flex flex-col h-full transition-transform duration-500 hover:-translate-y-1",
              };
              const inner = (
                <>
                  <div className="flex items-center gap-3 mb-5">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-gold/35 text-gold">
                      <Icon size={16} strokeWidth={1.4} />
                    </span>
                    <span className="text-[10px] tracking-[0.4em] uppercase text-gold/80">
                      {p.eyebrow}
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl md:text-[26px] text-themed leading-tight font-light">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-[11px] tracking-[0.3em] uppercase text-themed-muted">
                    {p.subtitle}
                  </p>
                  <p className="mt-5 text-themed-muted text-[14px] leading-relaxed flex-grow">
                    {p.body}
                  </p>
                  <span className="mt-7 btn-gold-sm inline-flex items-center gap-2 self-start">
                    {p.cta} <ArrowRight size={13} />
                  </span>
                </>
              );
              return (
                <Reveal key={p.id} delay={120 + i * 90}>
                  {p.isOrientationReset ? (
                    <button type="button" {...commonProps} style={{ textAlign: "left" }}>
                      {inner}
                    </button>
                  ) : (
                    <a href={p.href} {...commonProps}>{inner}</a>
                  )}
                </Reveal>
              );
            })}
          </div>
        </section>

        {/* ── Founder Note ─────────────────────────────────────── */}
        <section className="relative max-w-3xl mx-auto px-6 md:px-10 mt-32">
          <Reveal>
            <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6 text-center">
              From Cyrus
            </p>
            <div className="glass-card p-8 md:p-12 relative">
              <Quote
                size={28}
                strokeWidth={1.2}
                className="text-gold/40 absolute top-6 left-6"
                aria-hidden
              />
              <div className="mt-8 space-y-5 text-themed-muted leading-relaxed text-[15px] md:text-base">
                <p>
                  If you reached this page from a note I sent, or a post you
                  saw, thank you for following the thread. I don't write to
                  strangers in volume. I write because something in your work
                  or your timing pinged.
                </p>
                <p>
                  Authentic Momentum is the framework I built for people who
                  are tired of optimizing the wrong version of themselves.
                  The book names the friction. The Personal Action Plan maps the way
                  through. The Clarity Reset Session is the fastest way to find
                  out if any of this is for you, before money or hours get
                  spent on the wrong move.
                </p>
                <p>
                  Whichever path you pick, you'll find the same posture: no
                  pressure, real questions, plain language.
                </p>
              </div>
              <p className="mt-8 pt-6 border-t border-gold/10 text-themed text-sm font-serif">
                Cyrus Prescod
                <span className="block text-[10px] tracking-[0.3em] uppercase text-gold/80 mt-1">
                  Founder · Authentic Momentum
                </span>
              </p>
            </div>
          </Reveal>
        </section>

        {/* ── Proof (only if we have approved testimonials) ────── */}
        {voices.length > 0 && (
          <section
            className="relative max-w-6xl mx-auto px-6 md:px-10 mt-32"
            data-testid="linkedin-proof-section"
          >
            <Reveal>
              <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6 text-center">
                Voices on the Path
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-themed font-light text-center max-w-3xl mx-auto leading-tight">
                Real reflections.{" "}
                <span className="italic gold-gradient-text">No fabricated proof.</span>
              </h2>
            </Reveal>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
              {voices.map((t, i) => (
                <Reveal key={t.id || i} delay={120 + i * 80}>
                  <figure
                    data-testid={`linkedin-voice-${i}`}
                    className="glass-card h-full p-7 flex flex-col"
                  >
                    <Quote size={18} strokeWidth={1.4} className="text-gold/60 mb-4" aria-hidden />
                    <blockquote className="text-themed-muted text-[15px] leading-relaxed flex-grow">
                      {t.testimonial}
                    </blockquote>
                    <figcaption className="mt-6 pt-5 border-t border-gold/15">
                      <div className="text-themed text-sm font-serif">{t.name}</div>
                      {t.role ? (
                        <div className="text-[10px] tracking-[0.3em] uppercase text-gold/80 mt-1">
                          {t.role}
                        </div>
                      ) : null}
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
            <Reveal delay={400}>
              <div className="mt-10 text-center">
                <a
                  href="/voices"
                  data-testid="linkedin-voices-cta"
                  className="btn-ghost-gold inline-flex"
                >
                  Read More Voices
                </a>
              </div>
            </Reveal>
          </section>
        )}

        {/* ── Soft Lead Capture ─────────────────────────────────── */}
        <section className="relative max-w-3xl mx-auto px-6 md:px-10 mt-32" data-testid="linkedin-lead-capture-section">
          <LeadCaptureForm
            source="linkedin-landing"
            eyebrow="Not ready yet · Stay in the signal"
            headline="Not ready to pick a path?"
            subhead="Leave a sentence about where you are. Cyrus will respond with the next aligned step, when it's the right time. No automation. No newsletter sequence."
            testidPrefix="linkedin-lead-form"
          />
        </section>

        {/* ── Final CTA ─────────────────────────────────────────── */}
        <section className="relative max-w-3xl mx-auto px-6 md:px-10 mt-28 text-center">
          <Reveal>
            <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6">
              The simplest move
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-themed font-light leading-tight">
              When in doubt, take the{" "}
              <span className="italic gold-gradient-text">Snapshot.</span>
            </h2>
            <p className="mt-6 text-themed-muted leading-relaxed max-w-xl mx-auto">
              It's free, 90 seconds, and the most honest mirror Cyrus has
              built. Whatever you choose next will be clearer afterward.
            </p>
            <div className="mt-10">
              <a
                href="/frequency-snapshot"
                data-testid="linkedin-final-snapshot-cta"
                onClick={() => trackEvent("linkedin_final_snapshot_cta_clicked")}
                className="btn-gold inline-flex items-center gap-2"
              >
                Take the Clarity Snapshot <ArrowRight size={13} />
              </a>
            </div>
          </Reveal>
        </section>

      </main>
      <Footer />
      <OrientationResetCheckoutHost />
    </>
  );
};

export default LinkedInLandingPage;
